import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgClass, NgIf } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, FormRecord, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { EMPTY, Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';

import { DsBadgeComponent } from '@design-system/ds-badge/component/ds-badge.component';
import { DsButtonComponent } from '@design-system/ds-button/component/ds-button.component';
import { DsIconButtonComponent } from '@design-system/ds-icon-button/component/ds-icon-button.component';
import { DsInputComponent } from '@design-system/ds-input/component/ds-input.component';
import { DsSelectComponent } from '@design-system/ds-select/component/ds-select.component';
import { DsTextComponent } from '@design-system/ds-text/component/ds-text.component';
import { DsTitleComponent } from '@design-system/ds-title/component/ds-title.component';
import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';
import { ServiceRequestFileInterface } from '@interfaces/service-request/service-request-file.interface';
import { FileAttachmentTagStatuses } from '@enums/file-attachment-tag.enum';
import { UserRoleEnum } from '@enums/user-role.enum';
import { TablePaginationComponent } from '@modules/table-pagination/component/table-pagination.component';
import { TablePaginationInfoComponent } from '@modules/table-pagination-info/component/table-pagination-info.component';
import { NotificationService } from '@notification/service';
import { DsLoadingBarComponent } from '@design-system/ds-loading-bar/component/ds-loading-bar.component';
import { DsLoadingDotsComponent } from '@design-system/ds-loading-dots/component/ds-loading-dots.component';
import { DsLoadingSpinnerComponent } from '@design-system/ds-loading-spinner/component/ds-loading-spinner.component';
import { FileOriginBadgePipe } from '@pipes/file-origin-badge/file-origin-badge.pipe';
import { FileTagBadgeThemePipe } from '@pipes/file-tag-badge-theme/file-tag-badge-theme.pipe';
import { ToLocalDatePipe } from '@pipes/to-local-date/to-local-date.pipe';
import { LocalStorageService } from '@services/local-storage.service';
import { buildFileDisplayName, getFileBaseName, getFileExtension } from '@utils/file-name.util';
import { OrderByPipe } from '../../../../pipe/order-by.pipe';
import { AppCategory } from '../../../../service/service-request';
import { FileRowFormInterface } from './interfaces/file-row-form.interface';
import { FileUpdatePayloadInterface } from './interfaces/file-update-payload.interface';
import { ViewFilesModalDataInterface } from './interfaces/view-files-modal-data.interface';
import {
  ALL_FILE_TYPES_ID,
  VIEW_FILES_ACCEPT_ATTRIBUTE,
  VIEW_FILES_FETCH_PAGE_SIZE,
  VIEW_FILES_PAGE_SIZE,
} from './properties/view-files-modal.properties';
import { ViewFilesModalService } from './services/view-files-modal.service';
import { validateFile } from './utils/validate-file.util';
import { ViewFilesModalResult } from './types/view-files-modal-result.type';

@Component({
  selector: 'app-view-files-modal',
  templateUrl: './view-files-modal.component.html',
  styleUrls: ['./view-files-modal.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ViewFilesModalService, OrderByPipe],
  imports: [
    DsLoadingBarComponent,
    DsLoadingSpinnerComponent,
    DsLoadingDotsComponent,
    MatDialogModule,
    MatIconModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    NgIf,
    NgClass,
    ReactiveFormsModule,
    DsBadgeComponent,
    DsButtonComponent,
    DsIconButtonComponent,
    DsInputComponent,
    DsSelectComponent,
    DsTextComponent,
    DsTitleComponent,
    TablePaginationComponent,
    TablePaginationInfoComponent,
    FileOriginBadgePipe,
    FileTagBadgeThemePipe,
    ToLocalDatePipe,
  ],
})
export class ViewFilesModalComponent implements OnInit, OnDestroy {
  protected readonly dialogRef = inject(MatDialogRef<ViewFilesModalComponent, ViewFilesModalResult>);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly notificationService = inject(NotificationService);
  private readonly viewFilesModalService = inject(ViewFilesModalService);
  private readonly orderByPipe = inject(OrderByPipe);
  private readonly userRole = inject(LocalStorageService).getCurrentUserRole();
  private readonly destroy$ = new Subject<void>();

  readonly data: ViewFilesModalDataInterface = inject(MAT_DIALOG_DATA);

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  /** `accept` attribute of the hidden file input. */
  readonly acceptAttribute = VIEW_FILES_ACCEPT_ATTRIBUTE;

  readonly isReadOnly = this.userRole === UserRoleEnum.INTERNAL_READONLY || this.userRole === UserRoleEnum.ACCOUNTING;
  readonly isCustomerOrVendorCoordinator =
    this.userRole === UserRoleEnum.CUSTOMER_COORDINATOR || this.userRole === UserRoleEnum.VENDOR_COORDINATOR;
  /** Only the file passed into the modal is displayed */
  readonly isSingleFileView = !!this.data.selectedFile;
  /** The entity does not exist yet (e.g. service request creation) - files are returned to the parent instead of being uploaded */
  private readonly isDeferredUpload = !this.data.serviceRequestId;

  isLoading = false;
  isBulkEditActive = false;
  /**
   * A save request started from this modal is in flight. Every save action is blocked until it
   * settles: the row action and the bulk action reach the same upload, so guarding them separately
   * would still let one duplicate the file the other is uploading.
   */
  isSaving = false;
  files: ServiceRequestFileInterface[] = [];
  /** Rows rendered by the table: files that are not uploaded yet on top, the uploaded ones sorted and filtered below */
  displayedFiles: ServiceRequestFileInterface[] = [];
  fileAttachmentTagOptionList: AppCategory[];
  tagOptions: BaseDropdownInterface[] = [];
  sortColumn = 'fileName';
  sortOrder = true;
  readonly pageSize = VIEW_FILES_PAGE_SIZE;
  currentPage = 1;
  /** Number of files stored on the server, it matches the counter of the files tab of the parent page */
  totalCount = 0;
  /** Number of files left by the search and the file type filter, the pagination counts these */
  filteredCount = 0;

  /** Pinned files are listed first, no matter which column the table is sorted by */
  private readonly prioritySorting = 'isPinned';
  /** File property the table is sorted by, per sortable column */
  private readonly sortKeyByColumn: Record<string, string> = {
    fileName: 'displayName',
    createdOn: 'createdOn',
    fileAttachmentTagName: 'fileAttachmentTagName',
    creatorName: 'creatorName',
  };

  readonly allFileTypeOption: BaseDropdownInterface = { id: ALL_FILE_TYPES_ID, name: 'All' };
  fileTypeOptions: BaseDropdownInterface[] = [this.allFileTypeOption];
  /** Search by file name and file type filter */
  readonly filtersForm = new FormGroup({
    search: new FormControl<string>('', { nonNullable: true }),
    fileType: new FormControl<BaseDropdownInterface | null>(this.allFileTypeOption),
  });
  /** Inline edit forms of the rows that are currently edited, keyed by the file id or by the local id */
  readonly rowsForm = new FormRecord<FormGroup<FileRowFormInterface>>({});
  readonly compareById = (a: BaseDropdownInterface | null, b: BaseDropdownInterface | null): boolean => (a?.id ?? null) === (b?.id ?? null);
  /** Columns rendered by the files table, the tag column depends on the modal configuration */
  readonly displayedColumns: string[] = [
    'fileName',
    'createdOn',
    ...(this.data.isTagRequired ? ['fileAttachmentTagName'] : []),
    'creatorName',
    'actions',
  ];

  protected hasChanges = false;
  /** Snapshot of the files taken before a bulk edit, restored when the user cancels it */
  private filesSnapshot: ServiceRequestFileInterface[] = [];
  private localIdCounter = 0;

  ngOnInit(): void {
    this.subscribeOnFilters();

    if (this.data.serviceRequestId && this.data.fromPage) {
      this.loadFiles();
    }

    if (this.data.isTagRequired) {
      this.loadTagOptions();
    }
  }

  /** Re-renders the rows whenever the search or the file type filter changes */
  private subscribeOnFilters(): void {
    this.filtersForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.currentPage = 1;
      this.refreshDisplayedFiles();
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Current file name search value */
  get searchValue(): string {
    return this.filtersForm.controls.search.value;
  }

  /** Currently selected file extension, empty when every type is listed */
  get selectedFileTypeId(): string {
    const selectedId = this.filtersForm.controls.fileType.value?.id ?? ALL_FILE_TYPES_ID;

    return selectedId === ALL_FILE_TYPES_ID ? '' : selectedId;
  }

  /** Editing every row at once only makes sense when there is more than one file */
  get isBulkEditButtonVisible(): boolean {
    return this.files.length > 1 && !this.isBulkEditActive && !this.isSingleFileView && !this.isReadOnly;
  }

  /** Description of the empty state, it tells the user whether the filters are the reason for the empty list */
  get emptyStateDescription(): string {
    return this.hasActiveFilters ? 'Try changing the search or the file type filter.' : 'There are no files matching this view yet.';
  }

  private get hasActiveFilters(): boolean {
    return !!this.searchValue.trim() || !!this.selectedFileTypeId;
  }

  get isAddButtonVisible(): boolean {
    return !this.isBulkEditActive && !this.isReadOnly && this.data.isFileEditable;
  }

  trackByFn(index: number, item: ServiceRequestFileInterface): string {
    return item.fileId || item.localId || `idx-${index}`;
  }

  onPageChange(value: number): void {
    this.currentPage = value;
    this.refreshDisplayedFiles();
  }

  /**
   * Keeps the sorting state used by the `orderBy` pipe in sync with the material sort header.
   *
   * @param sort - sort state emitted by `matSort`
   */
  onSortChange(sort: Sort): void {
    this.sortColumn = sort.active;
    this.sortOrder = sort.direction === 'desc';
    this.currentPage = 1;
    this.refreshDisplayedFiles();
  }

  /** Opens the native file picker behind the `Add File` button */
  openFilePicker(): void {
    this.fileInput?.nativeElement.click();
  }

  /**
   * Adds the files picked in the native file picker to the list as not yet uploaded ones.
   *
   * @param event - input event of the file picker
   */
  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const selectedFiles = input?.files;

    if (!selectedFiles?.length) {
      return;
    }

    const acceptedFiles = this.rejectInvalidFiles(Array.from(selectedFiles));

    if (!acceptedFiles.length) {
      this.fileInput.nativeElement.value = '';

      return;
    }

    const defaultTagId =
      this.data.fromPage === 'Catering'
        ? (this.fileAttachmentTagOptionList?.find((tag) => tag.appCategoryName === FileAttachmentTagStatuses.cateringReceipt)
            ?.appCategoryId ?? null)
        : null;

    // the entries stay native `File` objects, the upload sends them as they are
    const addedFiles = acceptedFiles.map((file) => {
      // the MIME subtype of an office document is unusable as a file type (`vnd.openxmlformats-...`),
      // so the extension of the file name is the only reliable source
      const extension = getFileExtension(file.name);

      return Object.assign(file, {
        fileId: '',
        localId: `local-${++this.localIdCounter}`,
        fileType: extension,
        displayName: file.name ?? '',
        extension,
        fileName: getFileBaseName(file.name),
        fileAttachmentTagId: defaultTagId,
        isEditable: true,
      });
    });

    this.files = [...addedFiles, ...this.files] as unknown as ServiceRequestFileInterface[];
    (addedFiles as unknown as ServiceRequestFileInterface[]).forEach((file) => this.addRowForm(file));
    this.currentPage = 1;
    this.refreshFileTypeOptions();
    this.refreshDisplayedFiles();
    this.fileInput.nativeElement.value = '';
  }

  /**
   * Keeps the files that pass the extension and size validation and warns about every rejected one.
   *
   * @param files - files picked in the native file picker
   */
  private rejectInvalidFiles(files: File[]): File[] {
    return files.filter((file) => {
      const result = validateFile(file);

      if (!result.isValid) {
        this.notificationService.warning(result.message);
      }

      return result.isValid;
    });
  }

  /**
   * Switches a single file into the edit mode.
   *
   * @param file - file to edit
   */
  startFileEdit(file: ServiceRequestFileInterface): void {
    const index = this.files.findIndex((item) => item.fileId === file.fileId);

    this.files[index].isEditable = true;
    this.addRowForm(this.files[index]);
  }

  /**
   * Leaves the edit mode of a single file, a not yet uploaded file is discarded.
   *
   * @param file - edited file
   */
  cancelFileEdit(file: ServiceRequestFileInterface): void {
    this.removeRowForm(file);

    if (!file.fileId) {
      this.files = this.files.filter((item) => item !== file);
      this.refreshDisplayedFiles();

      if (!this.files.some((item) => item.isEditable)) {
        this.isBulkEditActive = false;
      }

      return;
    }

    const index = this.files.findIndex((item) => item.fileId === file.fileId);
    this.files[index].isEditable = false;

    if (!this.files.some((item) => item.isEditable)) {
      this.isBulkEditActive = false;
    }
  }

  /** Switches every file into the edit mode */
  startBulkEdit(): void {
    this.filesSnapshot = this.files.map((file) => ({ ...file }));

    this.files.forEach((file) => {
      file.isEditable = true;
      this.addRowForm(file);
    });

    this.isBulkEditActive = true;
  }

  /** Discards the bulk edit and restores the files as they were before it */
  cancelBulkEdit(): void {
    this.files = this.filesSnapshot;
    this.isBulkEditActive = false;
    this.clearRowForms();
    this.refreshDisplayedFiles();
  }

  /**
   * Removes a file: a not yet uploaded one is dropped from the list, an uploaded one is deleted on the server.
   *
   * @param file - file to remove
   */
  removeFile(file: ServiceRequestFileInterface): void {
    if (!file.fileId) {
      this.removeRowForm(file);
      this.files = this.files.filter((item) => item !== file);
      this.refreshDisplayedFiles();

      return;
    }

    this.viewFilesModalService
      .deleteFile(file.fileId)
      .pipe(
        catchError(() => {
          this.notificationService.error('Unable to delete file!');

          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.notificationService.success('File deleted successfully!', 'Success');
        this.hasChanges = true;
        this.loadFiles();
      });
  }

  /**
   * Toggles the pinned state of a file.
   *
   * @param file - file to pin or unpin
   */
  togglePin(file: ServiceRequestFileInterface): void {
    this.viewFilesModalService
      .togglePin(file.fileId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.hasChanges = true;
        this.currentPage = 1;
        this.loadFiles();
      });
  }

  /**
   * Downloads a file.
   *
   * @param file - file to download
   */
  downloadFile(file: ServiceRequestFileInterface): void {
    this.viewFilesModalService.downloadFile(file);
  }

  /** Uploads the newly added files and saves the changes of the already uploaded ones */
  saveFiles(): void {
    if (this.isSaving) {
      return;
    }

    this.applyEditedValues();

    if (this.rowsForm.invalid) {
      this.rowsForm.markAllAsTouched();
      this.notifyInvalidRows();

      return;
    }

    const filesToUpdate = this.applyUniqueDisplayNames();
    const newFiles = this.files.filter((file) => file.fileId === '');

    this.startSaving();

    this.uploadNewFiles(this.data.serviceRequestId ?? '', newFiles, filesToUpdate);
  }

  /**
   * Saves a single row: uploads a file that is not on the server yet, or saves the edits of an uploaded one.
   *
   * @param file - edited file
   */
  saveFile(file: ServiceRequestFileInterface): void {
    if (this.isSaving) {
      return;
    }

    this.applyEditedValues();

    // the entity does not exist yet, so the files are handed over to the caller as a whole batch
    if (this.isDeferredUpload) {
      this.saveFiles();

      return;
    }

    const rowForm = this.getRowForm(file);

    if (rowForm?.invalid) {
      rowForm.markAllAsTouched();
      this.notifyInvalidRows();

      return;
    }

    if (file.fileId) {
      this.updateFile(file);

      return;
    }

    this.uploadFile(file);
  }

  /** Tells the user what exactly keeps the edited rows from being saved */
  private notifyInvalidRows(): void {
    const hasInvalidName = Object.values(this.rowsForm.controls).some((rowForm) => rowForm.controls.baseName.invalid);

    this.notificationService.warning(hasInvalidName ? 'Enter a valid file name first!' : 'Assign tag to files first!');
  }

  /** Copies the values of the inline edit controls back into the files */
  private applyEditedValues(): void {
    this.files.forEach((file) => {
      const rowForm = this.getRowForm(file);

      if (!rowForm) {
        return;
      }

      // the extension is not editable, it is put back on top of the edited base name
      const extension = this.fileExtension(file);

      file.displayName = buildFileDisplayName(rowForm.controls.baseName.value, extension);
      file.extension = extension;
      file.fileType = extension || file.fileType;
      file.fileAttachmentTagId = rowForm.controls.fileAttachmentTag.value?.id ?? null;
    });
  }

  /**
   * Loads the files of the entity, keeping the files that are not uploaded yet.
   * The whole list is taken at once, the searching, filtering, sorting and paging happen on the client.
   */
  private loadFiles(): void {
    this.isLoading = true;

    const localFiles = (this.files ?? []).filter((file) => file.isLocal || (!file.fileId && file.isEditable));

    this.viewFilesModalService
      .loadFiles(this.data.fromPage, this.data.serviceRequestId, 1, VIEW_FILES_FETCH_PAGE_SIZE)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          const loadedFiles = (
            this.data.selectedFile ? response.items.filter((file) => file.fileId === this.data.selectedFile.fileId) : response.items
          ).map((file) => ({ ...file, extension: getFileExtension(file.displayName) || file.fileType }));

          this.files = [...loadedFiles, ...localFiles];
          this.pruneRowForms();
          this.totalCount = response.count;
          this.refreshFileTypeOptions();
          this.refreshDisplayedFiles();
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  /** Loads the file attachment tag options */
  private loadTagOptions(): void {
    this.viewFilesModalService
      .loadTagOptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.fileAttachmentTagOptionList = response.data;
          this.tagOptions = (response.data ?? []).map((tag) => ({ id: tag.appCategoryId, name: tag.appCategoryName }));
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.notificationService.warning('Unable to get the file attachment tag options!', 'Warning');
        },
      });
  }

  /**
   * Rebuilds the rows of the table out of the whole list of files: files that are not uploaded yet stay on
   * top in the order they were added, the uploaded ones are sorted, then the search and the file type filter
   * are applied and the requested page is cut out of what is left.
   */
  private refreshDisplayedFiles(): void {
    const pendingFiles = this.files.filter((file) => !file.fileId);
    const uploadedFiles = this.files.filter((file) => file.fileId);
    const sortedFiles: ServiceRequestFileInterface[] =
      this.orderByPipe.transform(uploadedFiles, this.sortKey, this.sortOrder, this.prioritySorting) ?? [];
    // a row that is not uploaded yet is still being edited, the search and the filter must not hide it
    const filteredFiles = [...pendingFiles, ...sortedFiles.filter((file) => this.matchesSearch(file) && this.matchesFileType(file))];

    this.filteredCount = filteredFiles.length;

    const lastPage = Math.max(1, Math.ceil(this.filteredCount / this.pageSize));

    if (this.currentPage > lastPage) {
      this.currentPage = lastPage;
    }

    const pageStart = (this.currentPage - 1) * this.pageSize;

    this.displayedFiles = filteredFiles.slice(pageStart, pageStart + this.pageSize);
  }

  /** File property the table is currently sorted by */
  private get sortKey(): string {
    return this.sortKeyByColumn[this.sortColumn] ?? this.sortColumn;
  }

  /**
   * Whether the file name contains the search value. Only the name is searched — a file has to be
   * findable by what the user sees, not by its path, its id or its upload date.
   *
   * @param file - listed file
   */
  private matchesSearch(file: ServiceRequestFileInterface): boolean {
    const search = this.searchValue.trim().toLowerCase();

    if (!search) {
      return true;
    }

    return (file.displayName || file.fileName || '').toLowerCase().includes(search);
  }

  /**
   * Whether the file has the currently selected file type.
   *
   * @param file - listed file
   */
  private matchesFileType(file: ServiceRequestFileInterface): boolean {
    const selectedFileType = this.selectedFileTypeId;

    if (!selectedFileType) {
      return true;
    }

    return this.getFileTypeId(file) === selectedFileType;
  }

  /** Rebuilds the file type filter options out of every file of the list */
  private refreshFileTypeOptions(): void {
    const fileTypes = [...new Set(this.files.map((file) => this.getFileTypeId(file)).filter(Boolean))].sort();

    this.fileTypeOptions = [this.allFileTypeOption, ...fileTypes.map((type) => ({ id: type, name: type.toUpperCase() }))];

    // the last file of the selected type could have just been deleted or renamed, the filter would filter nothing out
    if (this.selectedFileTypeId && !fileTypes.includes(this.selectedFileTypeId)) {
      this.filtersForm.controls.fileType.setValue(this.allFileTypeOption, { emitEvent: false });
    }
  }

  /**
   * File type of a file, taken from the extension of its name, so that a file without a readable
   * extension never turns into an empty option of the file type filter.
   *
   * @param file - listed file
   */
  private getFileTypeId(file: ServiceRequestFileInterface): string {
    return (getFileExtension(file.displayName) || file.extension || file.fileType || '').trim().toLowerCase();
  }

  /**
   * Makes the display names unique and collects the payload of the already uploaded files.
   *
   * @returns files that exist on the server and have to be updated
   */
  private applyUniqueDisplayNames(): FileUpdatePayloadInterface[] {
    const filesToUpdate: FileUpdatePayloadInterface[] = [];
    const displayNameCount = new Map<string, number>();
    // uploaded files keep their names, the counter suffix goes to the files that are added on top of them
    const orderedFiles = [...this.files.filter((file) => file.fileId), ...this.files.filter((file) => !file.fileId)];

    orderedFiles.forEach((file) => {
      const displayName = file.displayName;
      const count = displayNameCount.get(displayName);

      if (count) {
        displayNameCount.set(displayName, count + 1);
        // the counter goes in front of the extension, appending it to the whole name would break the file type
        file.displayName = buildFileDisplayName(`${getFileBaseName(displayName)}(${count})`, this.fileExtension(file));
      } else {
        displayNameCount.set(displayName, 1);
      }

      file.fileType = this.fileExtension(file) || file.fileType;

      if (file.fileId !== '') {
        filesToUpdate.push({
          fileId: file.fileId,
          fileName: file.displayName,
          fileType: file.fileType,
          fileAttachmentTagId: file.fileAttachmentTagId,
        });
      }
    });

    return filesToUpdate;
  }

  /**
   * Uploads the files that are not on the server yet, or saves the edited ones when there is nothing to upload.
   *
   * @param serviceRequestId - id of the entity the files belong to
   * @param newFiles - files that have to be uploaded
   * @param filesToUpdate - already uploaded files that have to be saved
   */
  private uploadNewFiles(
    serviceRequestId: string,
    newFiles: ServiceRequestFileInterface[],
    filesToUpdate: FileUpdatePayloadInterface[],
  ): void {
    if (this.isDeferredUpload) {
      this.stopSaving();
      this.closeWithDeferredFiles(newFiles);

      return;
    }

    if (!newFiles.length) {
      this.updateExistingFiles(filesToUpdate);

      return;
    }

    this.viewFilesModalService
      .uploadFiles(this.data.fromPage, serviceRequestId, newFiles)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.stopSaving()),
      )
      .subscribe({
        next: () => {
          this.clearRowForms();
          this.files = this.files.filter((file) => file.fileId);
          this.refreshDisplayedFiles();
          this.notificationService.success('File Added Successfully!', 'Success');
          this.loadFiles();
          this.dialogRef.close(true);
        },
        error: () => {
          this.notificationService.error('Failed to add file! Please try again');
        },
      });
  }

  /**
   * Uploads one not yet uploaded file, keeping the modal open for the remaining rows.
   *
   * @param file - file to upload
   */
  private uploadFile(file: ServiceRequestFileInterface): void {
    this.ensureUniqueDisplayName(file);
    file.fileType = this.fileExtension(file) || file.fileType;

    this.startSaving();

    this.viewFilesModalService
      .uploadFiles(this.data.fromPage, this.data.serviceRequestId, [file])
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.stopSaving()),
      )
      .subscribe({
        next: () => {
          this.removeRowForm(file);
          this.files = this.files.filter((item) => item !== file);
          this.refreshDisplayedFiles();
          this.hasChanges = true;
          this.notificationService.success('File Added Successfully!', 'Success');
          this.loadFiles();
        },
        error: () => {
          this.notificationService.error('Failed to add file! Please try again');
        },
      });
  }

  /**
   * Saves the name and the tag of one already uploaded file.
   *
   * @param file - file to save
   */
  private updateFile(file: ServiceRequestFileInterface): void {
    this.startSaving();

    this.viewFilesModalService
      .updateFiles([
        {
          fileId: file.fileId,
          fileName: file.displayName,
          fileType: file.fileType,
          fileAttachmentTagId: file.fileAttachmentTagId,
        },
      ])
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.stopSaving()),
      )
      .subscribe(() => {
        file.isEditable = false;
        this.removeRowForm(file);
        this.hasChanges = true;
        this.notificationService.success('File updated successfully!', 'Success');
        this.loadFiles();
      });
  }

  /**
   * Appends a counter to the display name while it collides with another file in the list.
   *
   * @param file - file about to be uploaded
   */
  private ensureUniqueDisplayName(file: ServiceRequestFileInterface): void {
    const usedNames = this.files.filter((item) => item !== file).map((item) => item.displayName);
    const baseName = getFileBaseName(file.displayName);
    const extension = this.fileExtension(file);
    let uniqueName = file.displayName;
    let counter = 1;

    while (usedNames.includes(uniqueName)) {
      // the counter goes in front of the extension, appending it to the whole name would break the file type
      uniqueName = buildFileDisplayName(`${baseName}(${counter})`, extension);
      counter += 1;
    }

    file.displayName = uniqueName;
  }

  /**
   * Saves the name and the tag of the already uploaded files.
   *
   * @param filesToUpdate - files to save
   */
  private updateExistingFiles(filesToUpdate: FileUpdatePayloadInterface[]): void {
    if (!filesToUpdate.length) {
      this.stopSaving();

      return;
    }

    this.viewFilesModalService
      .updateFiles(filesToUpdate)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.stopSaving()),
      )
      .subscribe(() => {
        this.notificationService.success('File updated successfully!', 'Success');
        this.loadFiles();
        this.dialogRef.close(true);
      });
  }

  /**
   * The parent entity is not created yet, so the files cannot be uploaded.
   * They are returned to the caller, which uploads them once the entity exists.
   *
   * @param newFiles - files picked in the modal
   */
  private closeWithDeferredFiles(newFiles: ServiceRequestFileInterface[]): void {
    const files = newFiles.map((file) =>
      Object.assign(file, { fileAttachmentTagName: this.tagNameById(file.fileAttachmentTagId), isLocal: true }),
    );

    this.dialogRef.close({ files });
  }

  /**
   * Edit form of a row, `null` when the row is not being edited.
   *
   * @param file - rendered file
   */
  getRowForm(file: ServiceRequestFileInterface): FormGroup<FileRowFormInterface> | null {
    return this.rowsForm.controls[this.rowKey(file)] ?? null;
  }

  private rowKey(file: ServiceRequestFileInterface): string {
    return file.fileId || file.localId;
  }

  /** Blocks every save action for the time of the request. */
  private startSaving(): void {
    this.isSaving = true;
    this.changeDetectorRef.markForCheck();
  }

  /** Releases the save actions, whatever the request ended with. */
  private stopSaving(): void {
    this.isSaving = false;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * A file name has to carry something readable — a name made of dots and spaces only
   * (`.`) is rendered as an empty name everywhere the file is listed.
   *
   * @param control - base name control of a row
   */
  private readonly fileBaseNameValidator = (control: AbstractControl): ValidationErrors | null => {
    const readableCharacters = ((control.value as string) ?? '').replace(/[\s.]/g, '');

    return readableCharacters ? null : { fileName: true };
  };

  /**
   * Extension of a file, kept out of the editable part of its name.
   *
   * @param file - listed file
   */
  private fileExtension(file: ServiceRequestFileInterface): string {
    return file.extension || getFileExtension(file.displayName);
  }

  /**
   * Puts a row into the edit mode by registering its form.
   *
   * @param file - file to edit
   */
  private addRowForm(file: ServiceRequestFileInterface): void {
    this.rowsForm.setControl(
      this.rowKey(file),
      new FormGroup<FileRowFormInterface>({
        baseName: new FormControl<string>(getFileBaseName(file.displayName), {
          nonNullable: true,
          validators: [Validators.required, this.fileBaseNameValidator],
        }),
        fileAttachmentTag: new FormControl<BaseDropdownInterface | null>(
          this.tagOptionById(file.fileAttachmentTagId),
          this.data.isTagRequired ? Validators.required : null,
        ),
      }),
    );
  }

  private removeRowForm(file: ServiceRequestFileInterface): void {
    this.rowsForm.removeControl(this.rowKey(file));
  }

  private clearRowForms(): void {
    Object.keys(this.rowsForm.controls).forEach((key) => this.rowsForm.removeControl(key));
  }

  /** Drops the edit forms of the files that are not listed anymore */
  private pruneRowForms(): void {
    const rowKeys = new Set(this.files.map((file) => this.rowKey(file)));

    Object.keys(this.rowsForm.controls).forEach((key) => {
      if (!rowKeys.has(key)) {
        this.rowsForm.removeControl(key);
      }
    });
  }

  private tagOptionById(tagId: string | null): BaseDropdownInterface | null {
    if (!tagId) {
      return null;
    }

    return this.tagOptions.find((option) => option.id === tagId) ?? { id: tagId, name: '' };
  }

  private tagNameById(tagId: string | null): string {
    return this.tagOptions.find((option) => option.id === tagId)?.name ?? '';
  }
}
